import React, { useState, useEffect, useCallback } from 'react';
import { Pagination, Modal } from 'react-bootstrap';
import { ExclamationTriangleFill, Calculator, ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import WithdrawIcon from '../img/withdraw.gif';
import '../CSS/Withdrawn.css';
import WithdrawnGuideModal from '../Components/WithdrawnGuideModal.jsx';

/*
  NOTE: This module assumes an axios/fetch API client exists elsewhere in
  your app (the same one used by schoolFeeAPI, studentAPI, etc).
  Below it calls the backend directly with fetch() against
  '/api/schoolfee/withdrawn-students' — adjust the URL/base path or swap
  in your existing api client (e.g. schoolFeeAPI.getWithdrawnStudents())
  to match how the rest of your app talks to the backend.
*/

const CLASS_OPTIONS = ['6', '7', '8', '9', '10', '11', '12'];

// ── Refund calculation constants (per client's rules) ──
const REGISTRATION_FEE = 200;       // Non-refundable, flat, all students
const MODULE_PACE_COST = 20;        // Per module/pace taken
const DAILY_RATE_SENIOR = 10;       // Grade 11 & 12
const DAILY_RATE_JUNIOR = 8.21;     // Grade 6 - 10

function getDailyRate(studentClass) {
  const cls = String(studentClass).trim();
  return (cls === '11' || cls === '12') ? DAILY_RATE_SENIOR : DAILY_RATE_JUNIOR;
}

// Inclusive day count from registration date to withdrawal date.
// e.g. registered Jan 1, withdrawn Jan 1 → 1 day. Jan 1 to Jan 2 → 2 days.
function getEnrolledDays(registrationDate, withdrawalDate) {
  const start = new Date(registrationDate);
  const end = new Date(withdrawalDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  return diffDays > 0 ? diffDays : 0;
}

function calculateRefund({ paidFee, studentClass, registrationDate, withdrawalDate, paces, damageCost, canteenCredits }) {
  const days = getEnrolledDays(registrationDate, withdrawalDate);
  const dailyRate = getDailyRate(studentClass);
  const dailyCost = days * dailyRate;
  const modulesCost = (parseFloat(paces) || 0) * MODULE_PACE_COST;
  const damage = parseFloat(damageCost) || 0;
  const canteen = parseFloat(canteenCredits) || 0;

  const totalDeductions = dailyCost + REGISTRATION_FEE + modulesCost + damage + canteen;
  const rawRefund = (parseFloat(paidFee) || 0) - totalDeductions;
  const refund = rawRefund > 0 ? rawRefund : 0;

  return {
    days,
    dailyRate,
    dailyCost,
    modulesCost,
    damage,
    canteen,
    totalDeductions,
    refund,
    eligible: rawRefund > 0
  };
}

function WithdrawnStudents() {
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [filterName, setFilterName] = useState('');
  const [filterClass, setFilterClass] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1
  });

  // Which student's refund calculator panel is currently open
  const [expandedStudentId, setExpandedStudentId] = useState(null);

  // Refund calculator inputs, per student — never sent to the backend,
  // purely local/in-memory for the live calculation
  const [refundInputs, setRefundInputs] = useState({});

  const getRefundInputs = (studentId) => (
    refundInputs[studentId] || { paces: '', damageCost: '', canteenCredits: '' }
  );

  const handleRefundInputChange = (studentId, field, value) => {
    setRefundInputs(prev => ({
      ...prev,
      [studentId]: {
        ...getRefundInputs(studentId),
        [field]: value
      }
    }));
  };

  const toggleExpanded = (studentId) => {
    setExpandedStudentId(prev => (prev === studentId ? null : studentId));
  };

  // Per-student save state for the refund calculator
  const [savingIds, setSavingIds] = useState({});     // { [studentId]: true } while a save is in flight
  const [saveStatus, setSaveStatus] = useState({});    // { [studentId]: { type: 'success'|'error', message, at } }
  const [deletingIds, setDeletingIds] = useState({});  // { [studentId]: true } while a delete is in flight

  // Delete confirmation modal state (mirrors the deleteModal pattern in SchoolFee.jsx)
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    studentId: null,
    studentName: ''
  });

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '0.00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const fetchWithdrawnStudents = useCallback(async (page = 1, name = filterName, cls = filterClass) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.itemsPerPage)
      });
      if (name) params.set('name', name);
      if (cls) params.set('class', cls);

      const response = await fetch(`/api/schoolfee/withdrawn-students?${params.toString()}`);
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to load withdrawn students');
      }

      setStudents(result.data || []);

      // Pre-fill inputs from a previously saved calculation (if one exists)
      // but never overwrite anything the user has already typed this session.
      setRefundInputs(prev => {
        const merged = { ...prev };
        (result.data || []).forEach(s => {
          if (s.savedAt && !merged[s.id]) {
            merged[s.id] = {
              paces: s.savedPacesTaken ?? '',
              damageCost: s.savedDamageCost ?? '',
              canteenCredits: s.savedCanteenCredits ?? ''
            };
          }
        });
        return merged;
      });

      setPagination(prev => ({
        ...prev,
        currentPage: result.page || 1,
        totalItems: result.total || 0,
        totalPages: result.totalPages || 1
      }));
    } catch (err) {
      console.error('Error fetching withdrawn students:', err);
      setErrorMsg('Could not load withdrawn students. Please try again.');
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  }, [filterName, filterClass, pagination.itemsPerPage]);

  useEffect(() => {
    fetchWithdrawnStudents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNameFilterChange = (e) => {
    const value = e.target.value;
    setFilterName(value);
    fetchWithdrawnStudents(1, value, filterClass);
  };

  const handleClassFilterChange = (e) => {
    const value = e.target.value;
    setFilterClass(value);
    fetchWithdrawnStudents(1, filterName, value);
  };

  const handlePageChange = (page) => {
    fetchWithdrawnStudents(page, filterName, filterClass);
  };

  const handleSaveRefund = async (student) => {
    const inputs = getRefundInputs(student.id);
    const result = calculateRefund({
      paidFee: student.amountPaid,
      studentClass: student.class,
      registrationDate: student.registrationDate,
      withdrawalDate: student.withdrawalDate,
      paces: inputs.paces,
      damageCost: inputs.damageCost,
      canteenCredits: inputs.canteenCredits
    });

    setSavingIds(prev => ({ ...prev, [student.id]: true }));
    setSaveStatus(prev => ({ ...prev, [student.id]: null }));

    try {
      const payload = {
        studentIdNumber: student.studentIdNumber,
        studentName: student.studentName,
        class: student.class,
        registrationDate: student.registrationDate,
        withdrawalDate: student.withdrawalDate,
        enrolledDays: result.days,
        dailyRate: result.dailyRate,
        dailyCost: result.dailyCost,
        registrationFee: REGISTRATION_FEE,
        pacesTaken: parseFloat(inputs.paces) || 0,
        pacesCost: result.modulesCost,
        damageCost: result.damage,
        canteenCredits: result.canteen,
        totalPaid: parseFloat(student.amountPaid) || 0,
        totalDeductions: result.totalDeductions,
        refundAmount: result.refund
      };

      const response = await fetch(`/api/schoolfee/withdrawn-students/${student.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save refund calculation');
      }

      setSaveStatus(prev => ({
        ...prev,
        [student.id]: { type: 'success', message: 'Saved successfully.' }
      }));

      // Reflect the new save locally so the delete button and "Last saved"
      // text appear immediately, without waiting for a re-fetch.
      setStudents(prev => prev.map(s => (
        s.id === student.id
          ? {
              ...s,
              savedRefundId: data.refundId,
              savedAt: new Date().toISOString(),
              savedPacesTaken: payload.pacesTaken,
              savedDamageCost: payload.damageCost,
              savedCanteenCredits: payload.canteenCredits,
              savedRefundAmount: payload.refundAmount
            }
          : s
      )));
    } catch (err) {
      console.error('Error saving refund calculation:', err);
      setSaveStatus(prev => ({
        ...prev,
        [student.id]: { type: 'error', message: 'Could not save — please try again.' }
      }));
    } finally {
      setSavingIds(prev => ({ ...prev, [student.id]: false }));
    }
  };

  // Opens the confirmation modal instead of a browser alert
  const openDeleteModal = (student) => {
    if (!student.savedRefundId) return;
    setDeleteModal({ show: true, studentId: student.id, studentName: student.studentName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, studentId: null, studentName: '' });
  };

  // Runs when the user confirms deletion inside the modal
  const confirmDeleteRefund = async () => {
    const { studentId } = deleteModal;
    if (!studentId) return;

    setDeletingIds(prev => ({ ...prev, [studentId]: true }));
    setSaveStatus(prev => ({ ...prev, [studentId]: null }));

    try {
      const response = await fetch(
        `/api/schoolfee/withdrawn-students/${studentId}/refund`,
        { method: 'DELETE' }
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete refund calculation');
      }

      // Clear the saved-calculation fields for this student locally
      setStudents(prev => prev.map(s => (
        s.id === studentId
          ? {
              ...s,
              savedRefundId: null,
              savedAt: null,
              savedPacesTaken: null,
              savedDamageCost: null,
              savedCanteenCredits: null,
              savedRefundAmount: null
            }
          : s
      )));

      // Reset the calculator inputs back to blank/zero for this student —
      // otherwise the previously entered paces/damage/canteen values would
      // stay on screen even though the saved record is gone.
      setRefundInputs(prev => ({
        ...prev,
        [studentId]: { paces: '', damageCost: '', canteenCredits: '' }
      }));

      setSaveStatus(prev => ({
        ...prev,
        [studentId]: { type: 'success', message: 'Saved calculation deleted.' }
      }));

      closeDeleteModal();
    } catch (err) {
      console.error('Error deleting refund calculation:', err);
      setSaveStatus(prev => ({
        ...prev,
        [studentId]: { type: 'error', message: 'Could not delete — please try again.' }
      }));
      closeDeleteModal();
    } finally {
      setDeletingIds(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // Summary totals across the currently loaded page's students —
  // gives a quick at-a-glance figure while browsing.
  const pageTotalOutstanding = students.reduce(
    (sum, s) => sum + (parseFloat(s.outstanding) || 0), 0
  );
  const pageTotalPaid = students.reduce(
    (sum, s) => sum + (parseFloat(s.amountPaid) || 0), 0
  );

  const renderPaginationItems = () => {
    const items = [];
    const { currentPage, totalPages } = pagination;
    for (let num = 1; num <= totalPages; num++) {
      if (
        num === 1 ||
        num === totalPages ||
        (num >= currentPage - 1 && num <= currentPage + 1)
      ) {
        items.push(
          <Pagination.Item
            key={num}
            active={num === currentPage}
            onClick={() => handlePageChange(num)}
          >
            {num}
          </Pagination.Item>
        );
      } else if (num === currentPage - 2 || num === currentPage + 2) {
        items.push(<Pagination.Ellipsis key={`ellipsis-${num}`} disabled />);
      }
    }
    return items;
  };

  return (
    <div className="wtd-page">
      <div className="wtd-main-container">
        <h3>
          Withdrawn Students
          <img className="animated-icons" src={WithdrawIcon} alt="Withdrawn students icon" />
        </h3>

        <div className="wtd-staff-alert">
          <ExclamationTriangleFill className="wtd-alert-icon" />
          This list shows withdrawn students only — expelled and passed-out students are not included.
          Figures reflect each student's fee record at the time of their last payment.
        </div>

        <div className="wtd-table-section">
          {/* Filter bar */}
          <div className="wtd-form-card">
            <div className="wtd-filter-input-wrap">
              <input
                type="text"
                className="wtd-filter-input"
                placeholder="Search by student name..."
                value={filterName}
                onChange={handleNameFilterChange}
              />
            </div>
            <div className="wtd-filter-input-wrap wtd-filter-input-wrap--narrow">
              <select
                className="wtd-filter-input"
                value={filterClass}
                onChange={handleClassFilterChange}
              >
                <option value="">All Classes</option>
                {CLASS_OPTIONS.map(cls => (
                  <option key={cls} value={cls}>Grade {cls}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary bar */}
          <div className="wtd-summary-bar">
            <div className="wtd-summary-item">
              <span className="wtd-summary-label">Withdrawn Students</span>
              <span className="wtd-summary-value">{pagination.totalItems}</span>
            </div>
            <div className="wtd-summary-item">
              <span className="wtd-summary-label">Paid (this page)</span>
              <span className="wtd-summary-value wtd-summary-paid">K{formatNumber(pageTotalPaid)}</span>
            </div>
            <div className="wtd-summary-item">
              <span className="wtd-summary-label">Outstanding (this page)</span>
              <span className="wtd-summary-value wtd-summary-outstanding">K{formatNumber(pageTotalOutstanding)}</span>
            </div>
          </div>

          {errorMsg && (
            <div className="wtd-error-banner">{errorMsg}</div>
          )}

          {/* Table */}
          <div className="wtd-table-container">
            <div className="wtd-table-wrapper">
              <table className="wtd-table">
                <thead className="wtd-table-header">
                  <tr>
                    <th>School ID</th>
                    <th>Student Name</th>
                    <th className="wtd-th-center">Class</th>
                    <th>Registration Date</th>
                    <th>Withdrawal Date</th>
                    <th className="wtd-th-center">Total Fee (K)</th>
                    <th className="wtd-th-center">Paid (K)</th>
                    <th className="wtd-th-center">Outstanding (K)</th>
                    <th className="wtd-th-center">Refund</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="9" className="wtd-empty-cell">Loading withdrawn students…</td>
                    </tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="wtd-empty-cell">No withdrawn students found.</td>
                    </tr>
                  ) : (
                    students.map(student => {
                      const isExpanded = expandedStudentId === student.id;
                      const inputs = getRefundInputs(student.id);
                      const result = calculateRefund({
                        paidFee: student.amountPaid,
                        studentClass: student.class,
                        registrationDate: student.registrationDate,
                        withdrawalDate: student.withdrawalDate,
                        paces: inputs.paces,
                        damageCost: inputs.damageCost,
                        canteenCredits: inputs.canteenCredits
                      });

                      return (
                        <React.Fragment key={student.id}>
                          <tr className="wtd-row">
                            <td>{student.studentIdNumber}</td>
                            <td>{student.studentName}</td>
                            <td className="wtd-th-center">{student.class}</td>
                            <td>{formatDate(student.registrationDate)}</td>
                            <td>{formatDate(student.withdrawalDate)}</td>
                            <td className="wtd-th-center">{formatNumber(student.totalFee)}</td>
                            <td className="wtd-th-center wtd-cell-paid">{formatNumber(student.amountPaid)}</td>
                            <td className="wtd-th-center wtd-cell-outstanding">{formatNumber(student.outstanding)}</td>
                            <td className="wtd-th-center">
                              <button
                                type="button"
                                className="wtd-refund-toggle-btn"
                                onClick={() => toggleExpanded(student.id)}
                              >
                                <Calculator className="wtd-refund-toggle-icon" />
                                {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                {student.savedAt && <span className="wtd-saved-dot" title="Calculation saved" />}
                              </button>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="wtd-refund-panel-row">
                              <td colSpan="9">
                                <div className="wtd-refund-panel">
                                  <div className="wtd-refund-panel-title">
                                    Refund Calculator — {student.studentName}
                                  </div>

                                  <div className="wtd-refund-grid">
                                    {/* Read-only breakdown */}
                                    <div className="wtd-refund-breakdown">
                                      <div className="wtd-refund-line">
                                        <span>Enrolled days ({formatDate(student.registrationDate)} → {formatDate(student.withdrawalDate)})</span>
                                        <span>{result.days} day(s)</span>
                                      </div>
                                      <div className="wtd-refund-line">
                                        <span>Daily rate (Grade {student.class})</span>
                                        <span>K{formatNumber(result.dailyRate)} / day</span>
                                      </div>
                                      <div className="wtd-refund-line">
                                        <span>Daily cost</span>
                                        <span>K{formatNumber(result.dailyCost)}</span>
                                      </div>
                                      <div className="wtd-refund-line">
                                        <span>Registration fee (non-refundable)</span>
                                        <span>K{formatNumber(REGISTRATION_FEE)}</span>
                                      </div>
                                      <div className="wtd-refund-line">
                                        <span>Modules/Paces cost ({inputs.paces || 0} × K20)</span>
                                        <span>K{formatNumber(result.modulesCost)}</span>
                                      </div>
                                      <div className="wtd-refund-line">
                                        <span>Damage cost</span>
                                        <span>K{formatNumber(result.damage)}</span>
                                      </div>
                                      <div className="wtd-refund-line">
                                        <span>Canteen credits</span>
                                        <span>K{formatNumber(result.canteen)}</span>
                                      </div>
                                      <div className="wtd-refund-line wtd-refund-line-total">
                                        <span>Total deductions</span>
                                        <span>K{formatNumber(result.totalDeductions)}</span>
                                      </div>
                                    </div>

                                    {/* User inputs */}
                                    <div className="wtd-refund-inputs">
                                      <div className="wtd-refund-input-group">
                                        <label>Modules/Paces Taken</label>
                                        <input
                                          type="number"
                                          min="0"
                                          className="wtd-refund-input"
                                          value={inputs.paces}
                                          onChange={(e) => handleRefundInputChange(student.id, 'paces', e.target.value)}
                                          placeholder="0"
                                        />
                                      </div>
                                      <div className="wtd-refund-input-group">
                                        <label>Damage Cost (K)</label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className="wtd-refund-input"
                                          value={inputs.damageCost}
                                          onChange={(e) => handleRefundInputChange(student.id, 'damageCost', e.target.value)}
                                          placeholder="0.00"
                                        />
                                      </div>
                                      <div className="wtd-refund-input-group">
                                        <label>Canteen Credits (K)</label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className="wtd-refund-input"
                                          value={inputs.canteenCredits}
                                          onChange={(e) => handleRefundInputChange(student.id, 'canteenCredits', e.target.value)}
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div className={`wtd-refund-result${result.eligible ? ' wtd-refund-result--eligible' : ' wtd-refund-result--none'}`}>
                                    <span className="wtd-refund-result-label">
                                      {result.eligible ? 'Refund Amount' : 'Not Eligible for Refund'}
                                    </span>
                                    <span className="wtd-refund-result-value">
                                      K{formatNumber(result.refund)}
                                    </span>
                                  </div>

                                  <div className="wtd-refund-save-row">
                                    <button
                                      type="button"
                                      className="wtd-refund-save-btn"
                                      disabled={!!savingIds[student.id]}
                                      onClick={() => handleSaveRefund(student)}
                                    >
                                      {savingIds[student.id] ? 'Saving…' : 'Save Refund Calculation'}
                                    </button>

                                    {student.savedRefundId && (
                                      <button
                                        type="button"
                                        className="wtd-refund-delete-btn"
                                        disabled={!!deletingIds[student.id]}
                                        onClick={() => openDeleteModal(student)}
                                      >
                                        {deletingIds[student.id] ? 'Deleting…' : 'Delete Saved Calculation'}
                                      </button>
                                    )}

                                    {saveStatus[student.id] && (
                                      <span className={`wtd-refund-save-status wtd-refund-save-status--${saveStatus[student.id].type}`}>
                                        {saveStatus[student.id].message}
                                      </span>
                                    )}

                                    {!saveStatus[student.id] && student.savedAt && (
                                      <span className="wtd-refund-save-status wtd-refund-save-status--info">
                                        Last saved {formatDate(student.savedAt)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="wtd-pagination-wrap">
              <Pagination>
                <Pagination.Prev
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                />
                {renderPaginationItems()}
                <Pagination.Next
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          Delete Confirmation Modal
      ══════════════════════════════════════════ */}
      <Modal show={deleteModal.show} onHide={closeDeleteModal}>
        <Modal.Header closeButton className="wtd-modal-header">
          <Modal.Title className="wtd-modal-title">Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="wtd-modal-body">
          <p>
            Are you sure you want to delete the saved refund calculation for {deleteModal.studentName}?
            This removes all saved history for this student and cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="wtd-modal-footer">
          <button className="wtd-btn wtd-btn-secondary" onClick={closeDeleteModal}>
            Cancel
          </button>
          <button
            className="wtd-btn wtd-btn-danger"
            onClick={confirmDeleteRefund}
            disabled={!!deletingIds[deleteModal.studentId]}
          >
            {deletingIds[deleteModal.studentId] ? 'Deleting…' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
      <WithdrawnGuideModal />
    </div>
  );
}

export default WithdrawnStudents;